import { isTbmFinalizedDocument } from '@safetyhub/shared';
import type { TBMStatus } from '../../lib/tbm';

/**
 * TBM 상세 chrome의 교육 진행 게이트 술어 (순수 함수).
 *
 * 2026-09-03 단일회차 재시작 정책(docs/decisions/2026-09-03-tbm-single-session-restart.md):
 * 단일회차도 확정 전이면 완료 세션이 있어도 교육이 "끝난" 것이 아니다 — 같은 배포 토큰으로
 * 다음 세션을 재시작할 수 있다(서버도 완료 시 토큰을 더 이상 비활성화하지 않음). 재시작 창은
 * 토큰 24시간 자연 만료와 확정(제출/완료/취소)이 닫는다.
 *
 * ⚠️ lib/tbm은 여러 spec이 vi.mock하는 모듈이라 이 술어를 거기 두면 팩토리 부분 mock에
 * 걸린다 — 별도 모듈 유지(런타임 의존은 없고 TBMStatus 타입만 가져온다).
 */
export interface TbmEducationGateInput {
  status: TBMStatus;
  /** 다회차 여부 — 판정 정본은 공유 술어 `isMultiSessionEducation`(end 날짜 > start 날짜). */
  isMultiSession: boolean;
  /** 다회차 교육 기간 만료 여부 (단일회차는 항상 false — 기간 개념 없음) */
  isEducationExpired: boolean;
  /** COMPLETED 상태의 교육 세션 존재 여부 */
  hasCompletedSession: boolean;
}

/**
 * 교육 종료 판정 — '교육 기록' 라벨(진행 불가) vs '교육 화면'(진행/재시작 가능).
 * 확정(APPROVED/PENDING_APPROVAL) 또는 다회차 기간 만료만 종료다.
 * [되돌림] 구 정책의 `(!isMultiSession && hasCompletedSession)` 절을 되살리면
 * 단일회차 완료 세션 케이스가 true로 뒤집혀 spec이 실패한다.
 */
export function isEducationDone(input: TbmEducationGateInput): boolean {
  return (
    input.status === 'APPROVED' ||
    input.status === 'PENDING_APPROVAL' ||
    (input.isMultiSession && input.isEducationExpired)
  );
}

/**
 * 배포 재전송(=재시작 안내) 노출 판정 — 상태·세션 축만 담당.
 * (담당 여부 isAssigned·진행자 존재 hasEducator는 호출부가 별도로 AND 한다)
 * 확정 전 상태(DRAFT)면 완료 세션이 있어도 허용 — 단일회차 재시작 정책.
 * [되돌림] 구 게이트 `(!hasCompletedSession || isMultiSession)`을 되살리면
 * 단일회차+완료 세션 케이스가 false로 뒤집혀 spec이 실패한다.
 *
 * ⚠️ 구 `|| input.status === 'REVIEWING'` 절은 그 상태가 문서 enum에서 사라지며 함께 걷혔다
 * (2026-09-08 `tbm-status-model-two-axes`). 담당자가 확인 중이던 문서는 `DRAFT`로 오므로
 * 판정 결과는 그대로다. (응답 alias `CREATED`는 없다 — 서버 어휘 `DRAFT` 그대로 온다.)
 */
export function canResendDistribution(input: TbmEducationGateInput): boolean {
  return input.status === 'DRAFT';
}

/**
 * 이 문서는 **확정**됐는가 — 교육 담당자 화면의 안내·버튼이 보는 축.
 *
 * 판정 정본은 공유 술어 `isTbmFinalizedDocument`이고 **서버의 토큰 쓰기 게이트가 같은 것을
 * 본다**(`tbm.service.ts`의 `assertEducatorWriteAllowed`). 그것이 이 얇은 래퍼의 존재 이유다 —
 * 화면이 자기 축을 손으로 적으면 «버튼은 없는데 API는 열려 있는» 상태로 갈린다.
 *
 * F-7(2026-09-09 dev 실측)이 그 상태였다: 확정본 담당자 화면이 「아래 "교육 시작" 버튼을 눌러
 * 오늘 교육을 진행하세요」라고 **없는 버튼**을 가리키는 동안(그 버튼의 게이트
 * `showStartButton`은 확정을 이미 보고 있어 렌더되지 않았다) 서버는 기록 추가에 201을 돌려줬다.
 * 안내 문구만 확정 축을 안 봤던 것이다.
 *
 * ⚠️ `isEducationDone`과 다른 물음이다 — 저쪽은 「교육을 더 진행할 수 있나」(다회차 기간 만료도
 * 종료로 친다)이고 이쪽은 「문서가 닫혔나」(기간은 축이 아니다)다. 기간이 남아 있어도 확정본에는
 * 쓸 수 없고, 확정 전이면 기간이 끝나도 문서는 열려 있다.
 */
export function isTbmEducationFinalized(status: TBMStatus): boolean {
  return isTbmFinalizedDocument(status);
}
